/**
 * PeriodesSaisonnieresPanel — item 6.5, mig 288. Gestion des périodes
 * d'horaires saisonniers (été, fêtes) depuis l'écran « Infos pratiques ».
 *
 * Ce panneau ne touche à aucun planning : déclarer une période fait deux
 * choses, et deux seulement —
 *   · pendant la période, les administrés voient les « horaires d'été » des
 *     services (quand ils sont renseignés) à la place des horaires normaux ;
 *   · l'encadrement de tous les services reçoit un rappel avant le début
 *     (appliquer les profils d'été) et avant la fin (les retirer — le rappel
 *     qu'on oublie, et la raison d'être du mécanisme).
 *
 * La bascule des plannings d'agents reste un geste HUMAIN : le temps de
 * travail est une décision d'employeur, pas un cron.
 */

import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Field, Input } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException, type PeriodeSaisonniere } from '@/lib/api';
import { Plus, Pencil, Trash2, Info, Sun } from 'lucide-react';

export function PeriodesSaisonnieresPanel() {
  const { confirm, dialogElement } = useConfirmDialog();
  const [periodes, setPeriodes] = useState<PeriodeSaisonniere[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [edit, setEdit] = useState<PeriodeSaisonniere | 'new' | null>(null);

  async function reload() {
    try {
      const r = await api.organisation.periodes.list();
      setPeriodes(r.periodes);
    } catch (e) {
      setErreur(e instanceof ApiException ? e.message : 'Chargement impossible');
    }
  }
  useEffect(() => { void reload(); }, []);

  async function supprimer(p: PeriodeSaisonniere) {
    const ok = await confirm({
      title: 'Supprimer cette période ?',
      description: `« ${p.nom} » ne déclenchera plus de rappel, et les horaires normaux resteront affichés.`,
      confirmLabel: 'Supprimer', danger: true,
    });
    if (!ok) return;
    try {
      await api.organisation.periodes.remove(p.id);
      await reload();
    } catch (e) {
      setErreur(e instanceof ApiException ? e.message : 'Suppression impossible');
    }
  }

  return (
    <div>
      {erreur && <Alert variant="error" className="mb-3">{erreur}</Alert>}

      <p className="mb-3 flex items-start gap-2 text-xs text-ink-subtle">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
        Pendant une période, les « horaires d'été » saisis sur chaque service
        remplacent les horaires normaux côté administré. Les plannings des
        équipes ne changent jamais tout seuls : l'encadrement reçoit un rappel
        avant le début et avant la fin, et applique lui-même les profils.
        Une période peut commencer le jour même (canicule, événement imprévu) :
        l'affichage bascule immédiatement et l'encadrement est prévenu une
        seule fois, tout de suite.
      </p>

      {periodes.length === 0 ? (
        <p className="text-sm text-ink-muted">Aucune période déclarée.</p>
      ) : (
        <ul className="space-y-2">
          {periodes.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 rounded-r-lg border border-border bg-surface/50 px-3 py-2">
              <div className="min-w-0">
                <span className="font-medium text-ink">{p.nom}</span>
                {p.enCours && <Badge variant="success" size="sm" className="ml-2"><Sun className="h-3 w-3" aria-hidden="true" /> En cours</Badge>}
                <div className="text-xs text-ink-subtle">
                  Du {p.dateDebut} au {p.dateFin} — rappels à J−{p.preavisJours} et J−3
                </div>
              </div>
              <div className="flex flex-shrink-0 gap-1">
                <Button variant="ghost" size="sm" onClick={() => setEdit(p)} aria-label={`Modifier ${p.nom}`}>
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => void supprimer(p)} aria-label={`Supprimer ${p.nom}`}>
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Button variant="primary" size="sm" className="mt-3" onClick={() => setEdit('new')}>
        <Plus className="h-4 w-4" aria-hidden="true" /> Déclarer une période
      </Button>

      {edit && (
        <PeriodeDialog
          initial={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); void reload(); }}
          onError={setErreur}
        />
      )}
      {dialogElement}
    </div>
  );
}

function PeriodeDialog({ initial, onClose, onSaved, onError }: {
  initial: PeriodeSaisonniere | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const [nom, setNom] = useState(initial?.nom ?? '');
  const [dateDebut, setDateDebut] = useState(initial?.dateDebut ?? '');
  const [dateFin, setDateFin] = useState(initial?.dateFin ?? '');
  const [preavis, setPreavis] = useState(String(initial?.preavisJours ?? 14));
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!nom.trim() || !dateDebut || !dateFin) { onError('Nom et dates requis'); return; }
    setBusy(true);
    try {
      const body = {
        nom: nom.trim(), dateDebut, dateFin,
        preavisJours: Math.min(60, Math.max(1, Number(preavis) || 14)),
      };
      if (initial) await api.organisation.periodes.update(initial.id, body);
      else await api.organisation.periodes.create(body);
      onSaved();
    } catch (e) {
      onError(e instanceof ApiException ? e.message : 'Enregistrement impossible');
    } finally { setBusy(false); }
  }

  return (
    <Dialog
      open onClose={onClose}
      title={initial ? 'Modifier la période' : 'Déclarer une période'}
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy}>
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Nom" htmlFor="per-nom" required>
          <Input id="per-nom" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Été 2026" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Début" htmlFor="per-debut" required>
            <Input id="per-debut" type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} />
          </Field>
          <Field label="Fin" htmlFor="per-fin" required>
            <Input id="per-fin" type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} />
          </Field>
        </div>
        <Field label="Préavis des rappels (jours)" htmlFor="per-preavis">
          <Input id="per-preavis" type="number" min={1} max={60} value={preavis}
                 onChange={(e) => setPreavis(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
